import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { posts } from "./blog-data";
import { usePageMeta } from "../hooks/usePageMeta";

const BlogHeader = () => (
  <header className="border-b border-black/5 bg-[#FDFCF8]/90 backdrop-blur-xl sticky top-0 z-40">
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
      <Link
        to="/"
        data-testid="blog-back-home"
        className="flex items-center"
      >
        <img
          src="/logo.jpg"
          alt="Neurological Communication Partners"
          className="h-14 w-auto rounded-xl ring-1 ring-black/5"
        />
      </Link>
      <Link
        to="/"
        className="text-sm text-[#5C6661] hover:text-[#2D332F] transition"
      >
        ← All sections
      </Link>
    </div>
  </header>
);

export default function Blog() {
  usePageMeta({
    title: "Resources & Writing on Neurological Speech Therapy",
    description:
      "Practical, clinically-grounded essays for adults & families — Parkinson's voice care, stroke recovery, swallowing, and what good adult speech therapy actually looks like.",
    path: "/blog",
  });
  return (
    <div className="min-h-screen bg-[#FDFCF8] text-[#2D332F]">
      <BlogHeader />
      <main className="max-w-5xl mx-auto px-6 md:px-10 py-14 md:py-20">
        <p className="text-xs uppercase tracking-[0.25em] text-[#5C6661] mb-4">
          Resources & writing
        </p>
        <h1
          data-testid="blog-title"
          className="font-serif text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05]"
        >
          Practical reading for
          <br />
          <em className="text-[#739072]">patients &amp; families.</em>
        </h1>
        <p className="mt-6 text-[#5C6661] text-lg leading-relaxed max-w-2xl">
          Short, clinically-grounded essays from Kathleen — on Parkinson&rsquo;s
          voice care, stroke recovery, swallowing, and what good adult speech
          therapy actually looks like.
        </p>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {posts.map((p, i) => (
            <motion.article
              key={p.slug}
              data-testid={`blog-post-card-${p.slug}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="group rounded-3xl border border-black/5 bg-white p-7 md:p-8 hover:-translate-y-1 hover:shadow-lg transition-all duration-500 flex flex-col"
            >
              <p className="text-xs uppercase tracking-[0.2em] text-[#739072] mb-4">
                {p.category} · {p.readTime}
              </p>
              <h2 className="font-serif text-xl md:text-2xl text-[#2D332F] leading-snug mb-3">
                {p.title}
              </h2>
              <p className="text-sm text-[#5C6661] leading-relaxed flex-1">
                {p.summary}
              </p>
              <Link
                to={`/blog/${p.slug}`}
                data-testid={`blog-read-${p.slug}`}
                className="mt-6 inline-flex items-center gap-2 text-sm text-[#2D332F] group-hover:text-[#739072] transition"
              >
                Read article
                <ArrowUpRight
                  size={14}
                  strokeWidth={1.5}
                  className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                />
              </Link>
            </motion.article>
          ))}
        </div>
      </main>
    </div>
  );
}
